import { mediaCard, mediaHero, photos } from "@/lib/media";
import { stock, stockCard, stockHero } from "@/lib/stock";

export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  /** 4:3 crop used on service cards. */
  image: string;
  /** Wide, high-resolution crop used as the full-bleed service page hero. */
  heroImage: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "residential-cleaning",
    title: "Residential Cleaning",
    subtitle: "Detail-driven cleaning for your home",
    price: "Free Estimate",
    image: stockCard(stock.residentialCleaning),
    heroImage: stockHero(stock.residentialCleaning),
    description:
      "Professional house cleaning across Northern Indiana and Southwest Michigan, built around your routine. Our team works room by room — kitchens, bathrooms, bedrooms, living spaces, and high-touch surfaces — so you come home to a spotless, fresh, healthier house every single visit.",
    features: [
      "Top-to-bottom kitchen & bathroom deep clean",
      "Ovens, appliances, tubs, tile & fixtures",
      "Bedrooms, living areas & high-touch surfaces",
      "Floor mopping, vacuuming & dusting",
      "Move-in / move-out and post-construction cleans",
      "Weekly, bi-weekly or monthly recurring plans",
    ],
    process: [
      { step: "Walkthrough", desc: "We confirm rooms, pets, and any priority zones so nothing is missed." },
      { step: "Custom Plan", desc: "Your cleaning checklist is tailored to your home, schedule, and budget." },
      { step: "Deep Clean", desc: "Our team follows a top-to-bottom system on every visit." },
      { step: "Final Check", desc: "We walk the home with you to confirm every detail meets our standards." },
    ],
  },
  {
    slug: "commercial-cleaning",
    title: "Commercial Cleaning",
    subtitle: "Offices, storefronts & rentals",
    price: "Free Estimate",
    image: stockCard(stock.commercialCleaning),
    heroImage: stockHero(stock.commercialCleaning),
    description:
      "Keep your office, storefront, or rental property spotless with Yessica Cleaning and Painting LLC. We schedule around your business hours so floors shine, restrooms sanitize, and your customers walk into a polished, professional space every day.",
    features: [
      "Offices, retail spaces & waiting rooms",
      "Restrooms sanitized & restocked",
      "Breakrooms, kitchens & common areas",
      "Floors, glass & high-touch disinfection",
      "Rental turnovers & post-construction cleanup",
      "Recurring daily, weekly or monthly service",
    ],
    process: [
      { step: "Site Visit", desc: "We tour your space to understand traffic, surfaces, and pain points." },
      { step: "Custom Quote", desc: "You receive a clear, flat-rate quote with no hidden fees." },
      { step: "Scheduled Service", desc: "Our team arrives on time, ready to work around your hours." },
      { step: "Quality Check", desc: "Routine quality checks keep your space spotless week after week." },
    ],
  },
  {
    slug: "interior-painting",
    title: "Interior Painting",
    subtitle: "Walls, ceilings, trim & doors",
    price: "Free Estimate",
    image: stockCard(stock.interiorPainting),
    heroImage: mediaHero(photos.interiorPaintWide),
    description:
      "A fresh coat changes everything. We paint interiors for homes and businesses across Northern Indiana and Southwest Michigan — walls, ceilings, trim, doors, and accent features — with careful prep, clean lines, and full protection for your floors and furniture.",
    features: [
      "Walls, ceilings, trim, doors & baseboards",
      "Drywall patching, caulking & surface prep",
      "Accent walls & color consultation",
      "Furniture and flooring fully protected",
      "Low-VOC, family & pet-safe paint options",
      "Residential and commercial interiors",
    ],
    process: [
      { step: "Color & Quote", desc: "We review colors, finishes, and square footage, then quote it flat-rate." },
      { step: "Prep & Protect", desc: "Furniture is covered, edges taped, and every surface properly prepped." },
      { step: "Paint", desc: "Primer plus the coats your surfaces actually need — never rushed." },
      { step: "Walkthrough", desc: "We inspect together, touch up anything you flag, and clean up fully." },
    ],
  },
  {
    slug: "exterior-painting",
    title: "Exterior Painting",
    subtitle: "Siding, porches, decks & garages",
    price: "Free Estimate",
    image: mediaCard(photos.exteriorHouse),
    heroImage: stockHero(stock.houseExterior),
    description:
      "Exterior painting that stands up to Indiana and Michigan weather. We handle siding, porches, decks, railings, garages, and outbuildings — washing and prepping every surface first so the finish bonds properly and lasts through the seasons.",
    features: [
      "Siding, soffits, fascia & shutters",
      "Porches, decks, railings & stairs",
      "Garages, sheds & outbuildings",
      "Scraping, sanding, priming & caulking",
      "Pressure washing included before prep",
      "Weather-rated exterior paints and stains",
    ],
    process: [
      { step: "Inspection", desc: "We check siding, wood, and trim for rot, peeling, and problem areas." },
      { step: "Wash & Prep", desc: "Surfaces are pressure washed, scraped, sanded, and primed." },
      { step: "Paint", desc: "Even, weather-rated coats applied to every surface and edge." },
      { step: "Final Walk", desc: "We review the finished exterior with you before we pack up." },
    ],
  },
  {
    slug: "cabinet-and-trim-painting",
    title: "Cabinet & Trim Painting",
    subtitle: "Kitchen cabinets, railings & millwork",
    price: "Free Estimate",
    image: stockCard(stock.kitchenCabinets),
    heroImage: stockHero(stock.kitchenCabinets),
    description:
      "Refinishing cabinets costs a fraction of replacing them and transforms a kitchen in days, not weeks. We degrease, sand, prime, and finish cabinet doors, boxes, railings, and millwork for a smooth, durable, factory-look surface.",
    features: [
      "Kitchen & bathroom cabinet refinishing",
      "Doors, drawer fronts & cabinet boxes",
      "Stair railings, spindles & newel posts",
      "Crown molding, casing & built-ins",
      "Degrease, sand, prime & finish coats",
      "Durable enamel finishes in any color",
    ],
    process: [
      { step: "Assessment", desc: "We check cabinet material and condition to confirm what's refinishable." },
      { step: "Strip & Sand", desc: "Doors come off, hardware is labeled, and every surface is degreased and sanded." },
      { step: "Prime & Finish", desc: "Bonding primer plus smooth enamel coats for a hard, even finish." },
      { step: "Reinstall", desc: "Doors and hardware go back on, aligned and ready to use." },
    ],
  },
  {
    slug: "pressure-washing",
    title: "Pressure Washing",
    subtitle: "Driveways, siding, decks & walkways",
    price: "Free Estimate",
    image: mediaCard(photos.pressureWashDriveway),
    // Their own mid-job driveway shot. It's a 1200px-wide portrait original, so
    // the hero is requested at native width rather than upscaled to 1920.
    heroImage: mediaHero(photos.pressureWashDriveway, 1200),
    description:
      "Years of dirt, algae, and weather stains come off in a single visit. We pressure wash driveways, sidewalks, patios, siding, decks, fences, and porches — restoring curb appeal and prepping surfaces properly when painting comes next.",
    features: [
      "Driveways, sidewalks & patios",
      "House siding, brick & soffits",
      "Decks, porches & fences",
      "Algae, mildew & dirt stain removal",
      "Surface prep before exterior painting",
      "Adjusted pressure for every surface type",
    ],
    process: [
      { step: "Inspection", desc: "We identify surface type and staining so we use the right pressure and mix." },
      { step: "Pre-Treat", desc: "Stubborn algae, mildew, and grease are pre-treated before washing." },
      { step: "Wash", desc: "Controlled, even passes that clean thoroughly without damaging the surface." },
      { step: "Rinse & Review", desc: "We rinse surrounding areas and walk the results with you." },
    ],
  },
];
